/**
 * Conversation Display - Shows AI avatar messages in theater style
 */
import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Avatar,
    Chip,
    IconButton,
    Tooltip,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText
} from '@mui/material';
import {
    EmojiEmotions,
    Psychology,
    TrendingUp
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';

import { ConversationMessage, UserReaction } from '../../types/matching';

interface ConversationDisplayProps {
    messages: ConversationMessage[];
    userReactions: UserReaction[];
    onReaction: (messageId: string, reactionType: string) => void;
    selectedMessage?: string;
    currentUserId?: string;
    user1Name?: string;
    user2Name?: string;
}

const ConversationDisplay: React.FC<ConversationDisplayProps> = ({
    messages,
    userReactions,
    onReaction,
    selectedMessage,
    currentUserId,
    user1Name,
    user2Name
}) => {
    const [reactionMenuAnchor, setReactionMenuAnchor] = useState<null | HTMLElement>(null);
    const [selectedMessageId, setSelectedMessageId] = useState<string>('');
    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const shouldStickToBottomRef = useRef(true);

    const handleScroll = () => {
        const container = messagesContainerRef.current;
        if (!container) return;

        const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
        shouldStickToBottomRef.current = distanceFromBottom < 80;
    };

    // Auto-scroll only while the viewer is already near the newest message.
    useEffect(() => {
        const container = messagesContainerRef.current;
        if (container && shouldStickToBottomRef.current) {
            container.scrollTop = container.scrollHeight;
        }
    }, [messages]);

    const handleReactionClick = (event: React.MouseEvent<HTMLElement>, messageId: string) => {
        setReactionMenuAnchor(event.currentTarget);
        setSelectedMessageId(messageId);
    };

    const handleReactionSelect = (reactionType: string) => {
        if (selectedMessageId) {
            onReaction(selectedMessageId, reactionType);
        }
        setReactionMenuAnchor(null);
        setSelectedMessageId('');
    };

    const getMessageReactions = (messageId: string) => {
        return userReactions.filter(reaction => reaction.message_id === messageId);
    };

    const getEmotionColor = (emotion: string): string => {
        const emotionColors: Record<string, string> = {
            excited: '#FF6B6B',
            happy: '#4ECDC4',
            curious: '#45B7D1',
            warm: '#FFA07A',
            passionate: '#FF1744',
            concerned: '#FF9800',
            surprised: '#9C27B0',
            thoughtful: '#607D8B',
            friendly: '#4CAF50',
            engaged: '#2196F3'
        };
        return emotionColors[emotion] || '#757575';
    };

    const isOwnAvatar = (message: ConversationMessage) => {
        if (currentUserId && message.sender_id) return message.sender_id === currentUserId;
        return isUser1(message.sender_name);
    };

    const isUser1 = (senderName: string) => {
        if (user1Name) return senderName === user1Name;
        // Fallback: first distinct sender is user1
        const firstSender = messages.find(m => m.sender_type === 'user_avatar')?.sender_name;
        return senderName === firstSender;
    };

    const getSenderAvatar = (message: ConversationMessage) => {
        const avatarColor = isOwnAvatar(message) ? '#1976d2' : '#d32f2f';

        return (
            <Avatar
                sx={{
                    bgcolor: avatarColor,
                    width: 48,
                    height: 48
                }}
            >
                {message.sender_name.charAt(0)}
            </Avatar>
        );
    };

    const MessageBubble: React.FC<{ message: ConversationMessage; index: number }> = ({ message, index }) => {
        const reactions = getMessageReactions(message.message_id);
        const isSelected = message.message_id === selectedMessage;
        const isOwn = isOwnAvatar(message);
        const isLeft = isOwn;

        return (
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                style={{
                    display: 'flex',
                    flexDirection: isLeft ? 'row' : 'row-reverse',
                    alignItems: 'flex-start',
                    gap: 12,
                    marginBottom: 16,
                    padding: '0 16px'
                }}
            >
                {/* Avatar */}
                <Box sx={{ flexShrink: 0 }}>
                    {getSenderAvatar(message)}
                </Box>

                {/* Message Content */}
                <Box sx={{ flex: 1, maxWidth: '70%' }}>
                    {/* Sender Name and Timestamp */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 0.5,
                            justifyContent: isLeft ? 'flex-start' : 'flex-end'
                        }}
                    >
                        <Typography variant="caption" color="text.secondary">
                            {message.sender_name}
                        </Typography>
                        <Typography variant="caption" color="text.disabled">
                            {format(new Date(message.timestamp), 'HH:mm')}
                        </Typography>
                        {message.compatibility_impact && message.compatibility_impact > 0.05 && (
                            <Tooltip title={`Compatibility impact: +${(message.compatibility_impact * 100).toFixed(1)}%`}>
                                <TrendingUp sx={{ fontSize: 14, color: 'success.main' }} />
                            </Tooltip>
                        )}
                    </Box>

                    {/* Message Bubble */}
                    <Paper
                        elevation={isSelected ? 8 : 2}
                        sx={{
                            p: 2,
                            bgcolor: isOwn ? '#1976d2' : '#d32f2f',
                            color: '#fff',
                            borderRadius: 2,
                            position: 'relative',
                            border: isSelected ? '2px solid #111827' : 'none'
                        }}
                    >
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            {message.content}
                        </Typography>

                        {/* Emotion Indicators */}
                        {message.emotion_indicators.length > 0 && (
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                                {message.emotion_indicators.map((emotion, idx) => (
                                    <Chip
                                        key={idx}
                                        label={emotion}
                                        size="small"
                                        sx={{
                                            bgcolor: getEmotionColor(emotion),
                                            color: 'white',
                                            fontSize: '0.7rem',
                                            height: 20
                                        }}
                                    />
                                ))}
                            </Box>
                        )}

                        {/* Reaction Button */}
                        <IconButton
                            size="small"
                            onClick={(e) => handleReactionClick(e, message.message_id)}
                            sx={{
                                position: 'absolute',
                                bottom: -8,
                                right: isLeft ? -8 : 'auto',
                                left: isLeft ? 'auto' : -8,
                                bgcolor: 'background.paper',
                                '&:hover': { bgcolor: 'background.default' }
                            }}
                        >
                            <EmojiEmotions fontSize="small" />
                        </IconButton>
                    </Paper>

                    {/* User Reactions */}
                    {reactions.length > 0 && (
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 0.5,
                                mt: 1,
                                justifyContent: isLeft ? 'flex-start' : 'flex-end'
                            }}
                        >
                            <AnimatePresence>
                                {reactions.map((reaction, idx) => (
                                    <motion.div
                                        key={reaction.reaction_id}
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        exit={{ scale: 0 }}
                                        transition={{ delay: idx * 0.1 }}
                                    >
                                        <Tooltip title={`${reaction.user_name} reacted with ${reaction.reaction_type}`}>
                                            <Chip
                                                label={getReactionEmoji(reaction.reaction_type)}
                                                size="small"
                                                variant="outlined"
                                                sx={{ fontSize: '0.8rem' }}
                                            />
                                        </Tooltip>
                                    </motion.div>
                                ))}
                            </AnimatePresence>
                        </Box>
                    )}
                </Box>
            </motion.div>
        );
    };

    const getReactionEmoji = (reactionType: string): string => {
        const reactionEmojis: Record<string, string> = {
            like: '👍',
            love: '❤️',
            laugh: '😂',
            surprise: '😮',
            concern: '😟'
        };
        return reactionEmojis[reactionType] || '👍';
    };

    return (
        <Box
            sx={{
                height: '100%',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'background.paper'
            }}
        >
            {/* Messages Container */}
            <Box
                ref={messagesContainerRef}
                onScroll={handleScroll}
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    py: 2,
                    '&::-webkit-scrollbar': {
                        width: 8
                    },
                    '&::-webkit-scrollbar-track': {
                        bgcolor: 'background.default'
                    },
                    '&::-webkit-scrollbar-thumb': {
                        bgcolor: 'divider',
                        borderRadius: 4
                    }
                }}
            >
                {messages.length === 0 ? (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                            gap: 2,
                            color: 'text.secondary'
                        }}
                    >
                        <Psychology sx={{ fontSize: 64, opacity: 0.5 }} />
                        <Typography variant="h6">AI Theater Ready</Typography>
                        <Typography variant="body2" textAlign="center">
                            Start the conversation to watch your AI avatars interact in real-time
                        </Typography>
                    </Box>
                ) : (
                    <AnimatePresence>
                        {messages.map((message, index) => (
                            <MessageBubble
                                key={message.message_id}
                                message={message}
                                index={index}
                            />
                        ))}
                    </AnimatePresence>
                )}
            </Box>

            {/* Reaction Menu */}
            <Menu
                anchorEl={reactionMenuAnchor}
                open={Boolean(reactionMenuAnchor)}
                onClose={() => setReactionMenuAnchor(null)}
                transformOrigin={{ horizontal: 'center', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
            >
                {[
                    { type: 'like', emoji: '👍', label: 'Like' },
                    { type: 'love', emoji: '❤️', label: 'Love' },
                    { type: 'laugh', emoji: '😂', label: 'Funny' },
                    { type: 'surprise', emoji: '😮', label: 'Surprising' },
                    { type: 'concern', emoji: '😟', label: 'Concerning' }
                ].map((reaction) => (
                    <MenuItem
                        key={reaction.type}
                        onClick={() => handleReactionSelect(reaction.type)}
                    >
                        <ListItemIcon>
                            <span style={{ fontSize: '1.2rem' }}>{reaction.emoji}</span>
                        </ListItemIcon>
                        <ListItemText>{reaction.label}</ListItemText>
                    </MenuItem>
                ))}
            </Menu>
        </Box>
    );
};

export default ConversationDisplay;
